import queryClient from '../../../ReactQuery/client';
import useToastedMutation from '../../../ReactQuery/useToastedMutation';
import { put } from '../../../redux/api';
import type { QuickImportProjectScanMethodsSettings } from '../../../../shared/settings/project/scanMethods';

import { getOrganizationQuickImportScanMethodsSettingsQueryKey } from './useOrganizationQuickImportScanMethodsSettings';

type UpdateOrganizationQuickImportScanMethodsSettingsParams = QuickImportProjectScanMethodsSettings & {
  organizationId: number;
};

async function updateOrganizationQuickImportScanMethodsSettings({
  organizationId,
  projectDefaultQuickImportVendoredDetectionEnabled,
  projectDefaultQuickImportSnippetDetectionEnabled,
}: UpdateOrganizationQuickImportScanMethodsSettingsParams): Promise<void> {
  await put<QuickImportProjectScanMethodsSettings, void>(
    `/api/organizations/${organizationId}/settings/projects/quick-import-scan-methods`,
    {
      projectDefaultQuickImportVendoredDetectionEnabled,
      projectDefaultQuickImportSnippetDetectionEnabled,
    },
  );
}

/**
 * Saves the org's default quick-import scan methods (vendored / snippet detection). Used by the
 * import flow's "Save this preference for future imports" option.
 *
 * Writes the saved values straight into the settings cache (not just `invalidateQueries`) so the
 * next time the scan-methods modal opens it prefills from fresh data. The modal's fields seed their
 * state once on mount from whatever the cache holds, and a bare invalidate would leave the stale
 * value cached (no observer to refetch while the modal is closed), so it would re-seed stale.
 */
export function useUpdateOrganizationQuickImportScanMethodsSettings() {
  return useToastedMutation({
    mutationFn: updateOrganizationQuickImportScanMethodsSettings,
    successToastMessage: 'Default scan methods saved',
    errorToastMessage: 'Failed to save default scan methods',
    onSuccess: (_result, variables) => {
      const queryKey = getOrganizationQuickImportScanMethodsSettingsQueryKey(variables.organizationId);
      queryClient.setQueryData<QuickImportProjectScanMethodsSettings>(queryKey, {
        projectDefaultQuickImportVendoredDetectionEnabled: variables.projectDefaultQuickImportVendoredDetectionEnabled,
        projectDefaultQuickImportSnippetDetectionEnabled: variables.projectDefaultQuickImportSnippetDetectionEnabled,
      });
      // Reconcile with server truth in the background (e.g. fields the org isn't entitled to are dropped).
      void queryClient.invalidateQueries({ queryKey });
    },
  });
}
